import { useAlertStore } from '../../store/alertStore'
import { useAlertDetail, useAlertGeoJSON } from '../../hooks/useAlerts'
import { HazardBadge, SeverityBadge, PriorityDot } from './AlertBadge'
import type { GeoJSONFeatureCollection } from '../../types/cap'

/** Convert HTML-formatted description text (e.g. NSW RFS <br /> separators) to plain text. */
function stripHtml(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function bboxFromGeoJSON(fc: GeoJSONFeatureCollection): [number, number, number, number] | null {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity

  function walk(coords: unknown): void {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number') {
      const [lon, lat] = coords as [number, number]
      if (lon < minLon) minLon = lon
      if (lat < minLat) minLat = lat
      if (lon > maxLon) maxLon = lon
      if (lat > maxLat) maxLat = lat
    } else {
      coords.forEach(walk)
    }
  }

  fc.features.forEach((f) => { if (f.geometry) walk((f.geometry as { coordinates: unknown }).coordinates) })
  return isFinite(minLon) ? [minLon, minLat, maxLon, maxLat] : null
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-2 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-800 dark:text-gray-200 min-w-0 break-words">{value}</span>
    </div>
  )
}

export default function AlertDetail() {
  const { selectedAlertId, selectAlert, flyTo, fitBounds } = useAlertStore()
  const { data: alert, isLoading } = useAlertDetail(selectedAlertId)
  const { data: geoJSON } = useAlertGeoJSON(selectedAlertId)

  if (!selectedAlertId) return null

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-950 border-l border-gray-200 dark:border-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Alert Detail</span>
        <button
          type="button"
          onClick={() => selectAlert(null)}
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-1"
          aria-label="Close detail"
        >
          ✕
        </button>
      </div>

      {isLoading && (
        <div className="text-center text-gray-500 text-sm py-12">Loading…</div>
      )}

      {alert && (
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Summary */}
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <PriorityDot score={alert.priority_score} />
              <HazardBadge hazardType={alert.hazard_type} />
              <SeverityBadge severity={alert.severity} />
              <span className="text-xs text-gray-500 uppercase tracking-wider">{alert.msg_type}</span>
            </div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug">
              {alert.headline ?? 'No headline'}
            </h2>
            {alert.region_name && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{alert.region_name}</p>
            )}
          </div>

          {/* Map link */}
          {(alert.centroid || geoJSON) && (
            <button
              type="button"
              onClick={() => {
                if (geoJSON) {
                  const bbox = bboxFromGeoJSON(geoJSON)
                  if (bbox && fitBounds) { fitBounds(bbox); return }
                }
                if (alert.centroid && flyTo) {
                  flyTo(alert.centroid.coordinates[0], alert.centroid.coordinates[1], 10)
                }
              }}
              className="text-xs text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              → Zoom to alert area
            </button>
          )}

          {/* Metadata */}
          <div className="space-y-1.5">
            <DetailRow label="Urgency" value={alert.urgency} />
            <DetailRow label="Certainty" value={alert.certainty} />
            <DetailRow label="Sent" value={formatDateTime(alert.sent_at)} />
            <DetailRow label="Expires" value={formatDateTime(alert.expires_at)} />
            <DetailRow label="Sender" value={alert.sender} />
            <DetailRow label="Status" value={alert.status} />
          </div>

          {/* Info blocks */}
          {alert.info_blocks.map((info) => (
            <div key={info.id} className="space-y-3">
              {info.description && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Description
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {stripHtml(info.description)}
                  </p>
                </div>
              )}
              {info.instruction && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Instructions
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {stripHtml(info.instruction)}
                  </p>
                </div>
              )}
              {info.web_url && (
                <a
                  href={info.web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline block"
                >
                  More information ↗
                </a>
              )}
            </div>
          ))}

          {/* Areas */}
          {alert.areas.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Affected Areas
              </h3>
              <ul className="space-y-0.5">
                {alert.areas.map((area) => (
                  <li key={area.id} className="text-sm text-gray-700 dark:text-gray-300">
                    {area.description ?? 'Unnamed area'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

